import { tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { filterPresent } from '@bp/shared/rxjs';

import { MerchantAdminFeature } from '@bp/shared-domains-identity';

import {
	Identity,
	IdentityFacade,
	sendResetAuthenticatorAppLinkSuccess,
	sendResetPasswordLinkSuccess
} from '@bp/admins-shared/domains/identity';

import { IntroPageStep } from '../models';

import { changeIntroPageStep } from './intro.actions';
import { IntroFacade } from './intro.facade';

@Injectable()
export class IntroEffects {
	navigateToIntroStep$ = createEffect(
		() => this._actions$.pipe(
			ofType(changeIntroPageStep),
			tap(({ step }) => void this._router.navigate([ '/intro', step.kebabName ])),
		),
		{ dispatch: false },
	);

	changeIntroStepBasedOnIncompleteIdentityPermission = this._identityFacade.incompleteIdentity$
		.pipe(filterPresent)
		.subscribe(incompleteIdentity => {
			if (incompleteIdentity.hasPermission(MerchantAdminFeature.createAccount))
				this._introFacade.setStep(IntroPageStep.createAccount);

			if (incompleteIdentity.hasPermission(MerchantAdminFeature.setPasswordOnLogin))
				this._introFacade.setStep(IntroPageStep.createPassword);

			if (incompleteIdentity.hasPermission(MerchantAdminFeature.setSecurityQuestionsAnswers))
				this._introFacade.setStep(IntroPageStep.setSecurityQuestionsAnswers);

			if (incompleteIdentity.hasPermission(MerchantAdminFeature.registerAuthenticator))
				this._introFacade.setStep(IntroPageStep.registerAuthenticatorApp);

			if (this._shouldGoToResetPasswordStep(incompleteIdentity))
				this._introFacade.setStep(IntroPageStep.resetPassword);

			if (incompleteIdentity.hasPermission(MerchantAdminFeature.resetAuthenticator))
				this._introFacade.setStep(IntroPageStep.resetAuthenticatorApp);

			if (incompleteIdentity.hasPermission(MerchantAdminFeature.otpVerify))
				this._introFacade.setStep(IntroPageStep.otpOnLogin);
		});

	onSendResetPasswordLinkSuccessGotoEmailSentPage$ = createEffect(
		() => this._actions$.pipe(
			ofType(sendResetPasswordLinkSuccess),
			tap(() => void this._introFacade.setStep(IntroPageStep.resetPasswordLinkSent)),
		),
		{ dispatch: false },
	);

	onSendResetAuthenticatorAppLinkSuccessGotoEmailSentPage$ = createEffect(
		() => this._actions$.pipe(
			ofType(sendResetAuthenticatorAppLinkSuccess),
			tap(() => void this._introFacade.setStep(IntroPageStep.resetAuthenticatorAppLinkSent)),
		),
		{ dispatch: false },
	);

	constructor(
		private readonly _actions$: Actions,
		private readonly _router: Router,
		private readonly _introFacade: IntroFacade,
		private readonly _identityFacade: IdentityFacade,
	) {}

	private _shouldGoToResetPasswordStep(incompleteIdentity: Identity): boolean {
		return [
			MerchantAdminFeature.resetPassword,
			MerchantAdminFeature.resetExpiredPassword,
		]
			.some(permission => incompleteIdentity.hasPermission(permission));
	}
}
